import { Plural, T, Var, useGT } from 'gt-react';
import { Progress } from '@/components/ui/progress';
import type { BulkSendItem, BulkSendResult } from './types';

interface BulkSendRailProps<TItem extends BulkSendItem> {
  eyebrowLabel: string;
  itemNounOne: string;
  itemNounOther: string;
  items: TItem[];
  results: Map<string, BulkSendResult>;
  currentIndex: number;
  phase: 'review' | 'summary';
  isSingle: boolean;
  isSendingAll: boolean;
  bulkSendProgress: number;
  onSelect: (index: number) => void;
}

export function BulkSendRail<TItem extends BulkSendItem>({
  eyebrowLabel,
  itemNounOne,
  itemNounOther,
  items,
  results,
  currentIndex,
  phase,
  isSingle,
  isSendingAll,
  bulkSendProgress,
  onSelect,
}: BulkSendRailProps<TItem>) {
  const gt = useGT();
  const resultEntries = Array.from(results.entries());
  const sentCount = resultEntries.filter(([, r]) => r.status === 'sent').length;
  const skippedCount = resultEntries.filter(
    ([, r]) => r.status === 'skipped',
  ).length;
  const failedCount = resultEntries.filter(
    ([, r]) => r.status === 'failed',
  ).length;
  const canceledCount = resultEntries.filter(
    ([, r]) => r.status === 'canceled',
  ).length;
  const pendingCount = Math.max(
    items.length - sentCount - failedCount - skippedCount - canceledCount,
    0,
  );
  const progressPercent =
    items.length > 0
      ? ((phase === 'summary'
          ? items.length
          : isSendingAll
            ? bulkSendProgress
            : results.size) /
          items.length) *
        100
      : 0;

  return (
    <aside className="w-60 shrink-0 flex flex-col border-r pr-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {eyebrowLabel}
      </p>
      <p className="mt-1 text-lg font-semibold">
        <Var>{items.length}</Var>{' '}
        <Plural n={items.length} one={itemNounOne} other={itemNounOther} />
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        <T context="bulk send dialog left rail status counts">
          <Var>{sentCount}</Var> sent · <Var>{pendingCount}</Var> pending
        </T>
        {failedCount > 0 && (
          <T context="bulk send dialog left rail failed count">
            {' · '}
            <Var>{failedCount}</Var> failed
          </T>
        )}
        {skippedCount > 0 && (
          <T context="bulk send dialog left rail skipped count">
            {' · '}
            <Var>{skippedCount}</Var> skipped
          </T>
        )}
        {canceledCount > 0 && (
          <T context="bulk send dialog left rail canceled count">
            {' · '}
            <Var>{canceledCount}</Var> canceled
          </T>
        )}
      </p>
      {!isSingle && <Progress value={progressPercent} className="mt-3 h-1.5" />}
      <ul className="mt-3 flex-1 min-h-0 overflow-y-auto -mx-1">
        {items.map((item, i) => {
          /* eslint-disable i18next/no-literal-string -- Tailwind utility classes only */
          const result = results.get(item.id);
          const status = result?.status;
          const isActive = i === currentIndex && phase === 'review';
          let dotClass = 'border border-muted-foreground';
          if (status === 'sent') dotClass = 'bg-green-500';
          else if (status === 'failed') dotClass = 'bg-red-500';
          else if (status === 'skipped') dotClass = 'bg-amber-500';
          else if (status === 'canceled') dotClass = 'bg-gray-400';
          else if (isActive) dotClass = 'bg-foreground';
          const isFinishedStatus =
            status === 'sent' || status === 'skipped' || status === 'canceled';
          const rowClass = [
            'w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
            'hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent',
            isActive ? 'bg-muted font-semibold border-l-2 border-primary' : '',
            isFinishedStatus && !isActive ? 'text-muted-foreground' : '',
            status === 'canceled' && !isActive ? 'line-through' : '',
            isSendingAll ? 'opacity-60 cursor-not-allowed' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onSelect(i)}
                disabled={isSendingAll || isActive || status === 'sent'}
                className={rowClass}
              >
                <span
                  className={`h-2 w-2 rounded-full shrink-0 ${dotClass}`}
                  aria-hidden
                />
                <span className="truncate">
                  {item.displayName ??
                    gt('(Unknown)', {
                      context: 'unknown item name placeholder',
                    })}
                </span>
              </button>
            </li>
          );
          /* eslint-enable i18next/no-literal-string */
        })}
      </ul>
    </aside>
  );
}
